<template>
    <v-card height="100%">
        <v-col>
            <InfoRows
                :rows="totalColumns"
                class="shrink mx-1"
            />
            <div v-if="detailedStatisticsOpen && totalTasks">
                <ProgressBar
                    :segments="taskProgressSegments"
                    :show-label="true"
                    class="mt-3 mx-1"
                />
                <InfoRows
                    v-if="tasksTotalAndLeftCount"
                    class="mx-1 mb-2"
                    :rows="[tasksTotalAndLeftCount]"
                />
                <InfoRows
                    :rows="taskProgressSegmentsAsVisualText"
                    class="mx-1"
                />
                <ProgressBar
                    :segments="billingDurations"
                    :height="1"
                    class="my-2 mx-1"
                />
                <InfoRows
                    :rows="billingDurations"
                    class="mx-1"
                />
            </div>

        </v-col>
    </v-card>

</template>

<script>

import ProgressBar from "../../components/Graphs/ProgressBar.vue";
import {formatTimeDuration} from "../../plugins/formatting";
import {computed} from "@vue/composition-api";
import {$t, $tc} from "../../i18n";
import InfoRows from "./Graphs/InfoRows.vue";

export default {
    name: 'ReportSummaryStatistics',
    methods: {formatTimeDuration},
    components: {InfoRows, ProgressBar},
    props: {
        totalColumns: {
            type: Array,
            default: () => []
        },
        billingInfo: {
            type: Object,
            default: () => {
            },
        },
        totalTasks: {
            type: Object,
            default: null
        },
        detailedStatisticsOpen: {
            type: Boolean,
            default: null
        }
    },
    setup(props) {
        const calculatePercentage = (value, totalValue) => {
            if (!totalValue) {
                return 0
            }
            const percentage = value / totalValue * 100
            if (percentage < 0.5) {
                // not visible
                return 0
            }
            return Math.round(percentage * 100) / 100

        }

        const roundPercentage = (percentage) => {
            return Math.round(percentage * 10) / 10
        }

        const taskProgressSegments = computed(() => {
            const total = props.totalTasks.total_tasks_count

            const overduePercentage = calculatePercentage(props.totalTasks.overdue_tasks_count, total)
            const toDoPercentage = calculatePercentage(props.totalTasks.to_do_tasks_count, total)
            const inProgressPercentage = calculatePercentage(props.totalTasks.in_progress_tasks_count, total)
            const donePercentage = 100 - overduePercentage - toDoPercentage - inProgressPercentage

            return [
                {
                    label: $t("Overdue"),
                    value: props.totalTasks.overdue_tasks_count,
                    percentage: overduePercentage,
                    color: "var(--v-pink-lighten1)",
                    progressColor: "var(--v-pink-lighten1)",
                    textColor: "var(--v-main-base)",
                },
                {
                    label: $t("Todo"),
                    value: props.totalTasks.to_do_tasks_count,
                    percentage: toDoPercentage,
                    color: "var(--v-royal-blue-lighten1)",
                    progressColor: "var(--v-royal-blue-lighten1)",
                    textColor: "var(--v-royal-blue-base)",
                },
                {
                    label: $t("In progress"),
                    value: props.totalTasks.in_progress_tasks_count,
                    percentage: inProgressPercentage,
                    color: "var(--v-primary-lighten2)",
                    progressColor: "var(--v-primary-lighten2)",
                    textColor: "var(--v-primary-base)",
                },
                {
                    label: $t("Done"),
                    value: props.totalTasks.completed_tasks_count,
                    percentage: donePercentage,
                    color: "var(--v-tretiary-lighten1)",
                    progressColor: "var(--v-tretiary-lighten1)",
                    textColor: "var(--v-main-base)",
                }
            ]
        })

        const taskProgressSegmentsAsVisualText = computed(() => {
            const taskProgressSegmentsCopy = [...taskProgressSegments.value]
            return taskProgressSegmentsCopy.map(segment => {
                return {
                    ...segment,
                    value: `${roundPercentage(segment.percentage)}%`
                }
            })
        })

        const billingDurations = computed(() => {
            const total = props.billingInfo.total
            const billable = props.billingInfo.billable
            const nonBillable = props.billingInfo.nonBillable

            return [
                {
                    valueColor: "var(--v-tretiary-base)",
                    color: "var(--v-tretiary-base)",
                    progressColor: "var(--v-tretiary-base)",
                    percentage: calculatePercentage(billable, total),
                    label: $t("Billable"),
                    value: formatTimeDuration(billable),
                    valueClasses: ['subtitle-2'],
                },
                {
                    valueColor: "var(--v-g4-base)",
                    color: "var(--v-g4-base)",
                    progressColor: "var(--v-g2-base)",
                    percentage: calculatePercentage(nonBillable, total),
                    label: $t("Non-billable"),
                    value: formatTimeDuration(nonBillable),
                    valueClasses: ['subtitle-2'],
                },
            ]
        })

        const tasksTotalAndLeftCount = computed(() => {
            const total = props.totalTasks.total_tasks_count
            if (!total) {
                return null
            }
            const completed_tasks = props.totalTasks.completed_tasks_count
            const not_done = total - completed_tasks
            const label = `${total} ` + $tc("task | tasks", total)
            const value = `${not_done} ${$t("Not done").toLocaleLowerCase()}`
            return {
                label: label,
                value: value,
                labelClasses: ['caption'],
                valueClasses: ['caption'],
            }
        })

        return {
            taskProgressSegments,
            billingDurations,
            tasksTotalAndLeftCount,
            taskProgressSegmentsAsVisualText
        }
    }
}
</script>

<style scoped lang="scss">
</style>